"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouteInfo } from "@hooks/pages/use-route-info";
import React, { PropsWithChildren } from "react";
import { NotLogged } from "@components/errors/not-logged";
import { useAppSelector } from "@lib/redux/hooks";
import { selectAuth } from "@features/application/app-selectors";

/**
 * DocumentLoginWrapper
 *
 * Purpose:
 * - Guards document pages by enforcing authentication where required.
 * - Allows anonymous access for routes that explicitly opt-in via `page.allowAnonymous`.
 *
 * Key behaviors:
 * - Reads the current route via `useRouteInfo()` to decide if the page is public.
 * - Reads auth state from Redux using `useAppSelector(selectAuth)`:
 *   - `isAuthenticated === undefined` means "auth status is still loading" (e.g., initial bootstrap/hydration) → show a spinner.
 *   - `isAuthenticated === false` on protected routes → show NotLogged screen.
 *   - `isAuthenticated === true` or `allowAnonymous` → render children.
 *
 * Why this exists:
 * - Centralizes login gating for any content wrapped under <DocumentLoginWrapper>.
 * - Prevents flashing of protected content before auth is known.
 *
 * Notes for maintainers:
 * - `useRouteInfo()` returns `page` metadata resolved from the pathname. When `page` is null or missing,
 *   we treat the page as protected by default unless `allowAnonymous` is explicitly set to true on the route.
 * - Prefer extending route configs with `allowAnonymous` for public pages to avoid coupling auth logic here.
 * - This component runs on the client to access router and store hooks (Next.js "use client").
 */
export const DocumentLoginWrapper = t0 => {
  const $ = _c(1);
  const {
    children
  } = t0;
  const {
    isAuthenticated,
    session: t1
  } = useAppSelector(selectAuth);
  const {
    user: t2
  } = t1;
  const {
    email
  } = t2 === undefined ? {} : t2;
  const page = useRouteInfo();
  if (page.page?.allowAnonymous) {
    return children;
  }
  if (isAuthenticated === undefined) {
    return null;
  }
  if (isAuthenticated === false || !email) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="h-8/12 w-full"><NotLogged /></div>;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    return t3;
  }
  return children;
};